import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { Animations } from '@fi-shared/utils/animations';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiSupportService } from '@fi-shared/fi-support/fi-support.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export enum ContactSupportOptionType {
    Account = 0,
    Content = 1,
    Technical = 2,
    Feedback = 3,
    ChatContact = 4,
    EmailContact = 5,
    FeedbackInternal = 6,
    FeedbackChainformation = 7,
}

export enum ContactSupportDialogSectionType {
    Main,
    ContactMethod,
    Email,
    Feedback,
}

@Component({
    selector: 'fi-contact-support-dialog',
    templateUrl: './fi-contact-support-dialog.component.html',
    styleUrls: ['./fi-contact-support-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.enterState(200, false)],
    host: { class: 'fi-contact-support-dialog' },
    standalone: false,
})
export class FiContactSupportDialogComponent
    extends FiDialogsContentBase<undefined, undefined>
    implements OnInit, OnDestroy
{
    ContactSupportDialogSectionType = ContactSupportDialogSectionType;
    currentDialogSectionType = ContactSupportDialogSectionType.Main;
    currentSupportOptionType: ContactSupportOptionType;
    private customerDataService: FiCustomerDataService;
    private chatService: FiChatService;
    private supportService: FiSupportService;

    ngOnInit() {
        this.setServices();
        this.subscribeToCurrentSectionSectionChange();
        this.subscribeToCurrentSupportOptionTypeChange();
    }

    private subscribeToCurrentSectionSectionChange() {
        this.supportService.currentSupportSectionType$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((currentSection: ContactSupportDialogSectionType) => {
                this.currentDialogSectionType = currentSection;
            });
    }

    private subscribeToCurrentSupportOptionTypeChange() {
        this.supportService.currentSupportOptionType$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((currentOption: ContactSupportOptionType) => {
                this.currentSupportOptionType = currentOption;
            });
    }

    setScreenByOptionType() {
        if (this.supportService.isCurrentUserSupportManager()) {
            // special case
            this.supportService.setCurrentSupportSection(ContactSupportDialogSectionType.Main);
            this.supportService.setCurrentSupportOptionType(undefined);
        }

        switch (this.currentSupportOptionType) {
            case ContactSupportOptionType.FeedbackChainformation:
            case ContactSupportOptionType.FeedbackInternal:
                this.supportService.setCurrentSupportSection(ContactSupportDialogSectionType.Feedback);
                this.supportService.setCurrentSupportOptionType(ContactSupportOptionType.Feedback);
                break;
            case ContactSupportOptionType.EmailContact:
                this.supportService.setCurrentSupportSection(ContactSupportDialogSectionType.ContactMethod);
                this.supportService.setCurrentSupportOptionType(ContactSupportOptionType.Account);
                break;
            default:
                this.supportService.setCurrentSupportSection(ContactSupportDialogSectionType.Main);
                this.supportService.setCurrentSupportOptionType(undefined);
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    isCurrentSection(type: ContactSupportDialogSectionType): boolean {
        return this.currentDialogSectionType === type;
    }

    openChatConversation() {
        this.chatService.triggerOpenOneToOneConversationByUserId(
            this.customerDataService.getCustomerData().SupportContact.SupportManagerId,
        );
        this.closeDialog();
    }

    private setServices() {
        this.customerDataService = this.injector.get(FiCustomerDataService);
        this.chatService = this.injector.get(FiChatService);
        this.supportService = this.injector.get(FiSupportService);
    }

    private resetCurrentSupportSectionAndOptionTypes() {
        this.supportService.setCurrentSupportSection(ContactSupportDialogSectionType.Main);
        this.supportService.setCurrentSupportOptionType(undefined);
    }

    ngOnDestroy$: Subject<void> = new Subject<void>();
    ngOnDestroy() {
        this.resetCurrentSupportSectionAndOptionTypes();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
